import { FC, Ref, useRef } from "react";

import { SidebarOptionsMenu, SidebarOptionsMenuProps } from "./SidebarOptionsMenu";

import { COURSE_CODE_ID_QUERY_PARAM } from "components/constants";
import { CourseType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { MenuDivider, MenuInstance, MenuItem, SubMenu } from "mds/components/Menu";
import { showConfirmationModal } from "mds/components/Modal";
import { CopySendArrowIcon, FolderIcon, PencilEditIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectCourseCodeGroupings } from "store/selectors/base";
import { selectSortedTermCoursesByCourseCode } from "store/selectors/section";
import { toastSuccessMessage } from "utils/alerts";
import { useQueryParamsManager } from "utils/routing";

interface CourseOptionsMenuProps extends SidebarOptionsMenuProps {
  courseCode: CourseType;
  onRename: () => void;
}

export const CourseCodeOptionsMenu: FC<CourseOptionsMenuProps> = ({
  courseCode,
  onRename,
  className,
  eventPrefix,
  kind,
}) => {
  const menuRef: Ref<MenuInstance> = useRef(null);
  const groupings = useAppSelector(selectCourseCodeGroupings);
  const termCourses = useAppSelector((s) => selectSortedTermCoursesByCourseCode(s, courseCode?.id));
  const hasCourses = termCourses.some((term) => term.courses.length > 0);
  const filteredGroupings = groupings.filter((grouping) => grouping.id !== courseCode.grouping_id);
  const { deleteQueryParams } = useQueryParamsManager();

  const handleMoveToGrouping = async (groupingId: string | null) => {
    await storeApi.course_codes.partial_update({
      id: courseCode.id,
      grouping_id: groupingId,
    });

    toastSuccessMessage(t("home.toasts.course_code_moved_one"));
    menuRef.current?.closeMenu();
  };

  const handleDelete = async () => {
    if (hasCourses) {
      return;
    }

    const didDelete = await showConfirmationModal({
      title: t("confirm_delete_modal.delete_item", { item: t("glossary.course_code") }),
      labelText: t("confirm_delete_modal.delete_label", { name: courseCode.title }),
    });

    if (didDelete) {
      await storeApi.course_codes.destroy(courseCode.id);

      toastSuccessMessage(t("home.toasts.course_code_deleted"));

      deleteQueryParams([COURSE_CODE_ID_QUERY_PARAM]);
    }
  };

  const moveToSubMenu = (
    <SubMenu
      label={
        <IconText iconStart={<CopySendArrowIcon />} text={t("sidebar_options_menu.move_to")} />
      }
      overflow="auto"
      withArrow
    >
      <div className="m-2 min-w-[200px]">
        <div className="pb-2 text-black-tint-40">
          {t("sidebar_options_menu.move_course_code_to")}
        </div>

        {filteredGroupings.map((grouping) => (
          <MenuItem key={grouping.id} onClick={() => handleMoveToGrouping(grouping.id)}>
            <IconText iconStart={<FolderIcon />} text={grouping.title} />
          </MenuItem>
        ))}

        {courseCode.grouping_id && (
          <MenuItem onClick={() => handleMoveToGrouping(null)}>
            {t("sidebar_options_menu.uncategorized")}
          </MenuItem>
        )}
      </div>
    </SubMenu>
  );

  return (
    <SidebarOptionsMenu
      className={className}
      eventPrefix={eventPrefix}
      instanceRef={menuRef}
      kind={kind}
      optionsMenuActions={{
        onDelete: handleDelete,
        isDeleteDisabled: hasCourses,
        deleteTooltipText: hasCourses
          ? t("sidebar_options_menu.cannot_delete_course_code_with_courses")
          : undefined,
      }}
      size="s"
    >
      <MenuItem onClick={onRename}>
        <IconText
          iconStart={<PencilEditIcon />}
          text={t("sidebar_options_menu.rename_course_code")}
        />
      </MenuItem>

      <MenuDivider />

      {moveToSubMenu}
    </SidebarOptionsMenu>
  );
};
